import React, { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Menu,
  ChevronLeft,
  ChevronRight,
  LogIn,
  Info,
  RotateCcw,
} from 'lucide-react';
import { useAnalyticsData } from '../../hooks/useAnalyticsData';
import { useAuth } from '../../context/AuthContext';
import { useAuthPrompt } from '../../context/AuthPromptContext';

import CalculatorTriggerButton from '../common/CalculatorTriggerButton';
import GlobalCommandPalette from '../common/GlobalCommandPalette';
import DateSelector from '../common/DateSelector';
import IndexTicker from '../common/IndexTicker';
import FiiDiiHeaderTicker from '../common/FiiDiiHeaderTicker';
import PageInfoModal, { openPageInfoModal } from '../common/PageInfoModal';
import DiwaliGreetingModal from '../common/DiwaliGreetingModal';

interface TopBarProps {
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  onToggleMobile: () => void;
}

const ROUTE_INFO: Record<string, { group: string; title: string }> = {
  '/': { group: 'Overview', title: 'Market Intelligence Desk' },
  '/dashboard': { group: 'Overview', title: 'Market Breadth Overview' },
  '/summary': { group: 'Overview', title: 'Market Intelligence Desk' },
  '/overview': { group: 'Market Internals & Breadth', title: 'Accumulation Ladder' },
  '/market-breadth': { group: 'Market Internals & Breadth', title: 'Market Breadth Desk' },
  '/breadth': { group: 'Market Internals & Breadth', title: 'Market Breadth Desk' },
  '/index-heatmap': { group: 'Market Internals & Breadth', title: 'Index Sector Heatmap' },
  '/mtf-positioning': { group: 'Market Internals & Breadth', title: 'MTF Positioning & Leverage' },
  '/ema-chart': { group: 'Market Internals & Breadth', title: 'EMA Breadth & Structure' },
  '/count': { group: 'Market Internals & Breadth', title: 'Advance vs Decline Breadth' },
  '/accumulation-chart': { group: 'Market Internals & Breadth', title: 'Accumulation vs Distribution' },
  '/industry': { group: 'Market Internals & Breadth', title: 'Industry & Sector Analysis' },
  '/sector': { group: 'Research', title: 'Sector Explorer' },
  '/sector-explorer': { group: 'Research', title: 'Sector Explorer' },
  '/opportunities': { group: 'Setups & Scanners', title: 'Breakout Scanner' },
  '/super-setups': { group: 'Setups & Scanners', title: 'Super Setups' },
  '/breakouts': { group: 'Setups & Scanners', title: 'Breakout Stocks' },
  '/volumeshockers': { group: 'Setups & Scanners', title: 'Volume Shockers' },
  '/volume-streaks': { group: 'Setups & Scanners', title: 'Volume Streaks' },
  '/price-movers': { group: 'Setups & Scanners', title: '30-Day Price Movers' },
  '/accumulation': { group: 'Setups & Scanners', title: 'Accumulation Scanner' },
  '/overboughtsold': { group: 'Setups & Scanners', title: 'Overbought & Oversold' },
  '/history': { group: 'Research', title: 'Stock Explorer' },
  '/watchlist': { group: 'Research', title: 'Watchlist & Focus Radar' },
  '/bulk-block-deals': { group: 'Research', title: 'Institutional Bulk & Block Deals' },
  '/corporate-feeds': { group: 'Research', title: 'Corporate Feeds' },
  '/results-calendar': { group: 'Research & Earnings', title: 'Results Calendar' },
  '/concall-calendar': { group: 'Research & Earnings', title: 'Results Calendar' },
  '/earnings-calendar': { group: 'Research & Earnings', title: 'Concall & Results Calendar' },
  '/concalls': { group: 'Research & Earnings', title: 'Earnings Concalls Desk' },
  '/admin': { group: 'Administration', title: 'Admin Control Panel' },
  '/admin/users': { group: 'Administration', title: 'Admin Control Panel' },
  '/guide': { group: 'Support', title: 'Trading Playbook' },
  '/contact-us': { group: 'Support', title: 'Contact Us' },
};

export const TopBar: React.FC<TopBarProps> = ({
  isCollapsed,
  onToggleCollapse,
  onToggleMobile,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { data, selectedDate, setSelectedDate } = useAnalyticsData();
  const { isAdmin, isAuthenticated, loading } = useAuth();
  const { openAuthPrompt } = useAuthPrompt();

  const latestAvailableDate = useMemo(() => {
    if (!data || data.length === 0) return '';
    const valid = data.map((d) => d.Date).filter(Boolean);
    valid.sort((a, b) => b.localeCompare(a));
    return valid[0] || '';
  }, [data]);

  const isHistorical = Boolean(selectedDate && latestAvailableDate && selectedDate !== latestAvailableDate);

  const currentMeta = useMemo(() => {
    const pathname = location.pathname;
    if (pathname.startsWith('/history/')) {
      const symbol = pathname.split('/history/')[1]?.toUpperCase();
      return { group: 'Research', title: symbol ? `Stock: ${symbol}` : 'Stock Explorer' };
    }
    if (pathname.startsWith('/sector/')) {
      const sec = decodeURIComponent(pathname.split('/sector/')[1] || '').trim();
      return { group: 'Research', title: sec ? `Sector: ${sec}` : 'Sector Explorer' };
    }
    if (pathname.startsWith('/opportunities')) {
      if (pathname.includes('/super-setups')) return { group: 'Setups & Scanners', title: 'Super Setups' };
      if (pathname.includes('/short-term')) return { group: 'Setups & Scanners', title: 'Short-Term Breakouts' };
      if (pathname.includes('/long-term')) return { group: 'Setups & Scanners', title: 'Long-Term Breakouts' };
      if (pathname.includes('/approaching')) return { group: 'Setups & Scanners', title: 'Approaching Breakouts' };
      return { group: 'Setups & Scanners', title: isAdmin ? 'Super Setups' : 'Breakout Scanner' };
    }
    return ROUTE_INFO[pathname] || { group: 'Analytics', title: 'NiftySutra' };
  }, [location.pathname, isAdmin]);

  const showDateSelector =
    location.pathname !== '/ema-chart' &&
    location.pathname !== '/accumulation-chart' &&
    location.pathname !== '/count' &&
    location.pathname !== '/corporate-feeds';

  return (
    <header className="ns-topbar" role="banner">
      {/* Left side: Navigation Toggles & Single Primary Header */}
      <div className="ns-topbar__left">
        {/* Mobile Hamburger Toggle (visible on < 1024px) */}
        <button
          type="button"
          onClick={onToggleMobile}
          className="ns-topbar__collapse-btn ns-topbar__mobile-toggle"
          title="Open Menu"
          aria-label="Open Navigation Menu"
        >
          <Menu size={18} />
        </button>

        {/* Desktop Sidebar Collapse Toggle (hidden on mobile) */}
        <button
          type="button"
          onClick={onToggleCollapse}
          className="ns-topbar__collapse-btn ns-topbar__desktop-toggle"
          title={isCollapsed ? 'Expand Sidebar' : 'Collapse Sidebar'}
          aria-label={isCollapsed ? 'Expand Sidebar' : 'Collapse Sidebar'}
        >
          {isCollapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
        </button>

        {/* Single primary page heading */}
        <div className="ns-topbar__title-area">
          <h1 className="ns-topbar__page-title">{currentMeta.title}</h1>
          <button
            type="button"
            className="ns-topbar__page-info-btn"
            onClick={() => openPageInfoModal(location.pathname)}
            title={`What does this page represent? (${currentMeta.title})`}
            aria-label={`About ${currentMeta.title}`}
          >
            <Info size={14} aria-hidden="true" />
          </button>
        </div>
      </div>

      {/* Right side: Global Stock Search, Freshness Badge, Calculator, Sign In */}
      <div className="ns-topbar__right">
        {/* Index Snapshot (Nifty 50 / Nifty 500 latest close) */}
        <IndexTicker />

        {/* FII & DII Cash Market Institutional Pulse Ticker */}
        <FiiDiiHeaderTicker />



        {/* Global Search Component */}
        <div
          data-tour="global-search"
          style={{ flexShrink: 1, minWidth: 0 }}
          onClickCapture={
            !isAuthenticated
              ? (e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  openAuthPrompt('/history', 'Stock Explorer');
                }
              : undefined
          }
        >
          <GlobalCommandPalette />
        </div>

        {/* Historical Session Alert & 1-Click Return to Latest */}
        {isHistorical && (
          <div className="ns-topbar__historical-banner" role="status">
            <span className="ns-topbar__historical-text">
              Archive: <strong>{selectedDate}</strong>
            </span>
            <button
              type="button"
              className="ns-topbar__historical-reset-btn"
              onClick={() => setSelectedDate(latestAvailableDate)}
              title={`Return to latest session (${latestAvailableDate})`}
              aria-label="Return to latest session"
            >
              <RotateCcw size={11} aria-hidden="true" />
              <span>Latest</span>
            </button>
          </div>
        )}

        {/* Market Session Selector & Freshness */}
        {showDateSelector ? (
          <DateSelector
            selectedDate={selectedDate}
            dates={data.map((item) => item.Date)}
            onDateChange={setSelectedDate}
            label=""
            id="topbar-date-select"
            compact
          />
        ) : null}

        {/* Sign In Button for unauthenticated/guest users */}
        {!loading && !isAuthenticated && (
          <button
            type="button"
            onClick={() =>
              navigate('/login', {
                state: {
                  from: location.pathname,
                  message: 'Please sign in to access your account.',
                },
              })
            }
            className="ns-topbar__signin-btn"
            title="Sign in to your account"
          >
            <LogIn size={14} aria-hidden="true" />
            <span>Sign In</span>
          </button>
        )}

        {/* Admin Calculator Trigger */}
        {isAdmin && <CalculatorTriggerButton />}
      </div>

      <PageInfoModal />
      <DiwaliGreetingModal />
    </header>
  );
};

export default TopBar;

